import { FC, useCallback, useState } from 'react';
import LoaderWrapper from '@/components/ui/LoaderWrapper/LoaderWrapper.tsx';
import { useTranslation } from 'react-i18next';
import useRestaurants from '@/api/useRestaurants.ts';
import { Alert, Button } from '@mui/material';
import RestaurantsTable from '@/components/functional/RestaurantsTable/RestaurantsTable.tsx';
import Toggle from '@/components/shared/Toggle/Toggle.tsx';
import AddRestaurantModal from '@/components/modals/Restaurant/AddRestaurantModal.tsx';
import DeleteRestaurantModal from '@/components/modals/Restaurant/DeleteRestaurantModal.tsx';
import EditRestaurantModal from '@/components/modals/Restaurant/EditRestaurantModal.tsx';

const RestaurantsPage: FC = () => {
  const { t } = useTranslation();
  const [selectedElements, setSelectedElements] = useState<number[]>([]);
  const {
    data: restaurants,
    isLoading: isLoadingRestaurants,
    isError: isErrorRestaurants,
    error: errorRestaurants,
  } = useRestaurants();

  const hasSelectedElement = selectedElements.length > 0;

  const handleSelectedElementChange = useCallback((selectedElements: number[]) => {
    setSelectedElements(selectedElements);
  }, []);

  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-3xl font-medium m-0 font-ubuntu">{t('pages.restaurants.title')}</h1>
      {isErrorRestaurants && <Alert severity="error">{errorRestaurants?.message}</Alert>}
      <div className="flex justify-end flex-wrap gap-2">
        <Toggle
          renderTrigger={({ toggle }) => (
            <Button onClick={toggle} color="info" variant="contained">
              {t('button.add')}
            </Button>
          )}
          renderContent={({ isOpen, handleClose }) => <AddRestaurantModal isOpen={isOpen} onClose={handleClose} />}
        />
        <Toggle
          renderTrigger={({ toggle }) => (
            <Button onClick={toggle} color="info" variant="contained" disabled={!hasSelectedElement}>
              {t('button.edit')}
            </Button>
          )}
          renderContent={({ isOpen, handleClose }) => (
            <EditRestaurantModal isOpen={isOpen} onClose={handleClose} restaurantId={selectedElements[0]} />
          )}
        />
        <Toggle
          renderTrigger={({ toggle }) => (
            <Button onClick={toggle} color="error" variant="contained" disabled={!hasSelectedElement}>
              {t('button.delete')}
            </Button>
          )}
          renderContent={({ isOpen, handleClose }) => (
            <DeleteRestaurantModal isOpen={isOpen} onClose={handleClose} selectedElement={selectedElements[0]} />
          )}
        />
      </div>
      <div>
        <LoaderWrapper isLoading={isLoadingRestaurants}>
          <RestaurantsTable selectable={true} onSelectedChange={handleSelectedElementChange} rows={restaurants ?? []} />
        </LoaderWrapper>
      </div>
    </div>
  );
};

export default RestaurantsPage;
